import DoneIcon from "@mui/icons-material/Done";
import {
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { ok } from "neverthrow";
import { match } from "ts-pattern";

import { mustParseAny } from "@thalamos/common";
import type { McaCapacityAssessmentMinimumFields } from "@thalamos/vision-common";
import {
  valueAndLabelSchemaDeprecated,
  yesNoSchema,
} from "@thalamos/vision-common";

/**
 * We need this table to be particularly compact on mobile devices
 */
const Cell = styled(TableCell)({
  border: "1px solid",
  padding: "0.5rem",
});

export type MentalCapacityTableProps = {
  /**
   * The assumption is that we're mounted in a form which satisfies McaCapacityAssessmentMinimumFields
   * although we will validate that with a schema.
   */
  formValues: Record<string, unknown>;
};

export const MentalCapacityTable = ({
  formValues,
}: MentalCapacityTableProps) => {
  const parseYesNo = (fieldName: string) =>
    ok(formValues[fieldName])
      .andThen(mustParseAny(valueAndLabelSchemaDeprecated(yesNoSchema)))
      .unwrapOr(undefined)?.value;

  const toAnswer = (value: "yes" | "no" | undefined) =>
    match(value)
      .with(undefined, () => null)
      .with("yes", () => true)
      .with("no", () => false)
      .exhaustive();

  // Derive the combined "Use and weigh" answer from separate fields
  const weighAnswer = parseYesNo("canWeighInformation");
  const useAnswer = parseYesNo("canUseInformation");
  const combinedUseAndWeighAnswer =
    weighAnswer === undefined || useAnswer === undefined
      ? null
      : weighAnswer === "yes" && useAnswer === "yes"
        ? true
        : false;

  // Simple fields that map directly from a single form value
  const simpleRows = (
    [
      {
        question: "Understand",
        fieldName: "canUnderstandInformation",
      },
      {
        question: "Retain",
        fieldName: "canRetainInformation",
      },
    ] as const satisfies Array<{
      question: string;
      fieldName: keyof McaCapacityAssessmentMinimumFields;
    }>
  ).map(({ question, fieldName }) => ({
    question,
    answer: toAnswer(parseYesNo(fieldName)),
  }));

  // The combined "Use and weigh" row
  const useAndWeighRow = {
    question: "Use and weigh",
    answer: combinedUseAndWeighAnswer,
  };

  // Remaining simple fields after "Use and weigh"
  const trailingRows = (
    [
      {
        question: "Communicate",
        fieldName: "canCommunicateDecision",
      },
      {
        question: "Impairment",
        fieldName: "impairmentDisturbance",
      },
    ] as const satisfies Array<{
      question: string;
      fieldName: keyof McaCapacityAssessmentMinimumFields;
    }>
  ).map(({ question, fieldName }) => ({
    question,
    answer: toAnswer(parseYesNo(fieldName)),
  }));

  const rows = [...simpleRows, useAndWeighRow, ...trailingRows];

  return <Inner rows={rows} />;
};

export type InnerProps = {
  rows: Array<{ question: string; answer: boolean | null }>;
};

export const Inner = ({ rows }: InnerProps) => {
  return (
    <Stack>
      <Typography>Review your answers so far:</Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <Cell>Assessment questions</Cell>
            <Cell sx={{ textAlign: "center" }}>No</Cell>
            <Cell sx={{ textAlign: "center" }}>Yes</Cell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(
            (row, index) =>
              row.answer !== null && (
                <TableRow key={row.question}>
                  <Cell>
                    <span>{index + 1}. </span>
                    {row.question}
                  </Cell>
                  <Cell sx={{ textAlign: "center" }}>
                    {!row.answer && <DoneIcon />}
                  </Cell>
                  <Cell sx={{ textAlign: "center" }}>
                    {row.answer && <DoneIcon />}
                  </Cell>
                </TableRow>
              ),
          )}
        </TableBody>
      </Table>
    </Stack>
  );
};
